import { prepareStreamDownload } from './firebaseService'

// Cloudflare generates the MP4 asynchronously; re-POSTing the downloads
// endpoint is how it reports progress, so `prepareStreamDownload` is the poll.
// A short reel is usually ready on the first or second look, so start tight
// and back off rather than hammering the Stream API for two minutes.
const POLL_MIN_MS = 1500
const POLL_MAX_MS = 5000
const POLL_TIMEOUT_MS = 120000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Name for Cloudflare's `filename` query parameter.
 *
 * Cloudflare accepts at most 120 characters of alphanumerics, hyphens and
 * underscores, and appends `.mp4` itself — so the extension is stripped here,
 * not added, and everything else (spaces, accents, dots, path separators)
 * collapses to a hyphen.
 */
function streamFileName(name) {
    const base = String(name || '')
        .replace(/\.[a-z0-9]{2,5}$/i, '')
        .replace(/[^A-Za-z0-9_-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 120)
    return base || 'video'
}

/**
 * Click a link the browser will save rather than navigate to.
 *
 * `download` is deliberately optional: it is ignored on cross-origin URLs, so
 * the Cloudflare path relies on the server's `Content-Disposition: attachment`
 * (which the `filename` query parameter turns on) instead. It still matters
 * for the blob URLs of the fallback path.
 *
 * Safe to call after an `await`: a download is not a popup, so it does not
 * need the click's transient user activation. `downloadVideo` below has
 * always relied on that, triggering the save after polling Cloudflare, and
 * the export page relies on it to re-mint a signed URL on click.
 */
export function clickDownloadLink(href, downloadName) {
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = href
    a.rel = 'noopener'
    if (downloadName) a.download = downloadName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

/**
 * Pull the whole file into memory and save it from a blob URL.
 *
 * This is the legacy path and the reason downloads broke: the Firebase Storage
 * object is read with XHR, so it is subject to the bucket's CORS policy, which
 * lists specific origins — the admin app and the public share page are not
 * always among them. Kept only for videos with no Cloudflare copy, where it is
 * the sole option. Note the bytes here are the original upload, so a `.webm`
 * capture stays `.webm`; only the Cloudflare path yields MP4.
 */
async function downloadViaBlob(url, fileName) {
    const response = await fetch(url)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const blob = await response.blob()
    const objectUrl = window.URL.createObjectURL(blob)
    try {
        clickDownloadLink(objectUrl, fileName || 'download')
    } finally {
        window.URL.revokeObjectURL(objectUrl)
    }
}

/**
 * Recover the Stream uid embedded in a rendition URL.
 *
 * Mirrors `downloadUidFromUrl` in functions/lib/cloudflare.js. A cached URL
 * outlives the Stream copy it points at — a re-encode mints a new uid and
 * leaves the old rendition dead — so the uid is what says whether a remembered
 * URL still belongs to this video.
 */
export function downloadUidFromUrl(url) {
    // Both forms: the `/downloads/` URL Cloudflare advertises, and the `/dl/`
    // URL it redirects to — the only one a browser can fetch.
    const match =
        /^https:\/\/customer-[^.]+\.cloudflarestream\.com\/([^/]+)\/(?:downloads|dl)\//
            .exec(url || '')
    return match ? match[1] : null
}

/**
 * Vet a remembered rendition URL before trusting it.
 *
 * Two ways it can be wrong, and neither is worth a round-trip to find out the
 * hard way: it can belong to a Stream copy that has since been replaced (the
 * uid check, free), or the rendition can have been deleted (the HEAD, one
 * cheap request instead of a callable round-trip plus a Cloudflare API call).
 *
 * @param {?string} cached The resolved URL remembered on the document
 *   (`cfDirectUrl`).
 * @param {?string} expectedUid The document's current `cf.uid`.
 * @return {Promise<?string>} The URL if it is still good, otherwise null.
 */
export async function usableCachedUrl(cached, expectedUid) {
    if (!cached) return null
    if (expectedUid && downloadUidFromUrl(cached) !== expectedUid) return null
    try {
        const resp = await fetch(cached, { method: 'HEAD' })
        return resp.ok ? cached : null
    } catch {
        // A network or CORS failure tells us nothing about the rendition, so
        // fall back to the callable rather than declaring the cache dead.
        return null
    }
}

/**
 * Drive Cloudflare's MP4 rendition to `ready` and return both forms of where it
 * lives. Private: callers want one form or the other, never the choice.
 *
 * Split out of `downloadVideo` because sharing needs the same URL without the
 * download click: the share sheet wants the bytes as a `File`, not a link the
 * browser saves. Callers that only want the file on disk should keep using
 * `downloadVideo` — it never reads the bytes through JS, which is the whole
 * point of the Cloudflare path.
 *
 * Renditions are minted once and kept — re-POSTing reports on the existing one
 * rather than starting another — so once a document remembers its URL there is
 * nothing left to ask Cloudflare. That shortcut matters most on the share path,
 * where the callable round-trip is spent inside the click's transient user
 * activation window.
 *
 * Two URLs come back because they are not interchangeable. `url` is the
 * `/downloads/` endpoint: a 302 with no CORS headers, fine to navigate to and
 * the only one that takes a `filename` parameter, but impossible to `fetch`.
 * `directUrl` is what that redirects to — a plain 200 with CORS — which is the
 * only one the share path can read bytes from.
 *
 * @param {object} opts
 * @param {object} opts.source `{ shareId }` or `{ kind, id }`.
 * @param {function(string): void} [opts.onStatus] Called with `'preparing'`
 *   on each poll that finds the rendition unfinished.
 * @return {Promise<{url: string, directUrl: ?string}>} Where the rendition
 *   lives, in both forms.
 */
async function preparedRendition({ source, onStatus }) {
    let result = await prepareStreamDownload(source)
    const deadline = Date.now() + POLL_TIMEOUT_MS
    let wait = POLL_MIN_MS

    while (result.status !== 'ready' && Date.now() < deadline) {
        onStatus?.('preparing')
        await sleep(wait)
        wait = Math.min(wait * 1.5, POLL_MAX_MS)
        result = await prepareStreamDownload(source)
    }

    if (result.status !== 'ready' || !result.url) {
        throw new Error(`Cloudflare rendition ${result.status}`)
    }
    return { url: result.url, directUrl: result.directUrl || null }
}

/**
 * Where to point a download link.
 *
 * Always the `/downloads/` endpoint: it is the only form that accepts the
 * `filename` parameter that makes Cloudflare answer with an attachment. Always
 * via the callable, too — there is nothing worth caching here. A download is a
 * navigation behind a spinner that already says "Preparing download…", so the
 * round-trip is invisible, and skipping it would mean either persisting a URL
 * that is derivable from `cf.uid` anyway or threading the customer code through
 * every call site.
 */
export async function resolveStreamDownloadUrl({ source, onStatus }) {
    return (await preparedRendition({ source, onStatus })).url
}

/**
 * A rendition URL the browser can actually `fetch`.
 *
 * Cache-first, because this one is spent inside the click's transient user
 * activation window — the share sheet has to open before it expires. The vetted
 * value is `cfDirectUrl`, never the `/downloads/` URL: that is a 302 with no
 * CORS headers, so a HEAD against it can only ever fail and would report every
 * cache as dead.
 *
 * @param {object} opts
 * @param {object} opts.source `{ shareId }` or `{ kind, id }`.
 * @param {string} [opts.cachedDirect] `cfDirectUrl` from the document, if any.
 * @param {string} [opts.cfUid] The document's `cf.uid`.
 * @param {function(string): void} [opts.onStatus] Progress callback.
 * @return {Promise<string>} A fetchable URL.
 */
export async function resolveStreamFetchableUrl({ source, cachedDirect, cfUid, onStatus }) {
    const known = await usableCachedUrl(cachedDirect, cfUid)
    if (known) return known

    const { directUrl } = await preparedRendition({ source, onStatus })
    // An older deployment answers without one. Nothing here can resolve the
    // redirect, so say so and let the caller fall back to sharing a link.
    if (!directUrl) throw new Error('Cloudflare rendition has no fetchable URL')
    return directUrl
}

/**
 * Save a video to disk, preferring its Cloudflare Stream copy.
 *
 * Cloudflare serves the MP4 straight to the browser as an attachment, so the
 * file never passes through JS and no CORS preflight is involved — and what
 * lands on disk is H.264 MP4 rather than the original capture format.
 *
 * @param {object} opts
 * @param {object} [opts.source] `{ shareId }` or `{ kind, id }`. Pass it only
 *   when the document has a ready Cloudflare copy; omit to force the fallback.
 * @param {string} [opts.url] Firebase Storage URL, used if `source` is absent
 *   or Cloudflare fails.
 * @param {string} [opts.fileName] Suggested name for the saved file.
 * @param {function(string): void} [opts.onStatus] Called with `'preparing'`
 *   while Cloudflare encodes, then `'downloading'`.
 */
export async function downloadVideo({ source, url, fileName, onStatus }) {
    if (source) {
        try {
            const downloadUrl = await resolveStreamDownloadUrl({ source, onStatus })

            onStatus?.('downloading')
            // The redirecting form, deliberately: `filename` is what flips
            // Cloudflare to an attachment response, and it is only understood
            // there. A link click is a navigation, so the redirect that blocks
            // the share path's fetch costs nothing here.
            const name = encodeURIComponent(streamFileName(fileName))
            clickDownloadLink(`${downloadUrl}?filename=${name}`)
            return
        } catch (err) {
            if (!url) throw err
            console.warn('Cloudflare download failed, falling back to storage:', err)
        }
    }

    if (!url) throw new Error('No download URL available')
    onStatus?.('downloading')
    await downloadViaBlob(url, fileName)
}
